import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    ShoppingBag,
    TrendingDown,
    Gift,
    Bell,
    ChevronRight,
    CheckCircle2,
    Star,
    LayoutDashboard,
    Scan,
    Camera,
    ShieldCheck,
    Zap
} from 'lucide-react';
import { formatCurrency } from '../utils/formatUtils';

const PartnerDashboard = () => {
    const [partnerData, setPartnerData] = useState(null);
    const [activeLists, setActiveLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [scannerMode, setScannerMode] = useState('photo'); // 'photo' or 'live'
    const [me, setMe] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchMe = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const res = await axios.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } });
                setMe(res.data);
            } catch (e) { console.error('Failed to fetch me', e); }
        };
        fetchMe();
    }, []);

    const openScanner = (mode) => {
        setScannerMode(mode);
        setIsScannerOpen(true);
    };

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const config = {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                };

                // Fetch partner info and inviter's stats
                const profileRes = await axios.get('/api/profile/partner', config);
                setPartnerData(profileRes.data);

                // Fetch active shared lists
                const listsRes = await axios.get('/api/shopping/lists/shared', config);
                setActiveLists(listsRes.data);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);

                // If 401, maybe redirect to login or show specific error?
                // But for now just toast.
                if (error.response && error.response.status === 401) {
                    toast.error('Oturum süresi dolmuş, lütfen tekrar giriş yapın.');
                    // optionally navigate('/login');
                } else {
                    toast.error('Dashboard verileri yüklenemedi.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            </div>
        );
    }

    const myAvatar = me?.avatar || `https://ui-avatars.com/api/?name=${me?.name || 'Ben'}&background=random`;
    // Partner's baby info comes from partnerData.partner
    const babyActive = partnerData?.partner?.hasBaby;
    const babyAge = partnerData?.partner?.babyAgeGroup;

    return (
        <div className="p-4 md:p-8 space-y-8 animate-in fade-in duration-500">
            {/* Header Removed as per request */}

            {/* Motivation Widget */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl p-6 text-white shadow-xl shadow-green-500/20 relative overflow-hidden">
                <div className="relative z-10 flex items-start justify-between">
                    <div className="space-y-4">
                        <div className="bg-white/20 p-2 rounded-xl w-fit">
                            <TrendingDown size={28} />
                        </div>
                        <div>
                            <h3 className="text-lg font-medium opacity-90">Birlikte Kazandınız!</h3>
                            <p className="text-2xl font-bold leading-tight">
                                {partnerData?.partner?.name || 'Partneriniz'} bu mutfakta tam <span className="text-yellow-200">{formatCurrency(partnerData?.partner?.totalRescued || 0)}</span> değerinde gıda kurtardı! 💰
                            </p>

                        </div>
                    </div>
                    <div className="hidden md:block">
                        <CheckCircle2 size={120} className="opacity-10 -rotate-12" />
                    </div>
                </div>
                {/* Decorative Pattern */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
            </div>

            {/* Hızlı Araçlar & Güvenlik Paneli */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Bebek Kalkanı Status Card */}
                <div className={`p-5 rounded-2xl border flex items-center justify-between transition-all shadow-sm ${partnerData?.partner?.hasBaby ? 'bg-blue-50 border-blue-100 dark:bg-blue-900/10 dark:border-blue-800' : 'bg-gray-50 border-gray-100 dark:bg-slate-800 dark:border-slate-700'}`}>
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${partnerData?.partner?.hasBaby ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-500 dark:bg-slate-700'}`}>
                            <ShieldCheck size={24} />
                        </div>
                        <div>
                            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">Bebek Kalkanı</div>
                            <div className={`text-sm font-bold ${partnerData?.partner?.hasBaby ? 'text-blue-700 dark:text-blue-300' : 'text-gray-500'}`}>
                                {partnerData?.partner?.hasBaby ? `AKTİF (${partnerData.partner.babyAgeGroup || 'Hesaplanıyor'})` : 'PASİF'}
                            </div>
                        </div>
                    </div>
                    {partnerData?.partner?.hasBaby && (
                        <div className="flex gap-1">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span>
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse delay-75"></span>
                        </div>
                    )}
                </div>

                {/* Allergen Scanner: Photo */}
                <button
                    onClick={() => openScanner('photo')}
                    className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 flex items-center gap-4 hover:border-rose-200 dark:hover:border-rose-900/30 hover:bg-rose-50/30 dark:hover:bg-rose-900/5 transition-all shadow-sm group"
                >
                    <div className="p-2 bg-rose-50 dark:bg-rose-900/20 rounded-xl text-rose-500 group-hover:scale-110 transition-transform">
                        <Camera size={24} />
                    </div>
                    <div className="text-left">
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">Alerjen Taraması</div>
                        <div className="text-sm font-bold text-gray-900 dark:text-white">Fotoğraf Çek</div>
                    </div>
                </button>

                {/* Allergen Scanner: Live */}
                <button
                    onClick={() => openScanner('live')}
                    className="p-5 rounded-2xl bg-gradient-to-r from-rose-500 to-pink-600 text-white flex items-center gap-4 hover:brightness-110 transition-all shadow-md shadow-rose-500/20 group"
                >
                    <div className="p-2 bg-white/20 rounded-xl text-white group-hover:rotate-12 transition-transform">
                        <Scan size={24} />
                    </div>
                    <div className="text-left">
                        <div className="text-xs font-bold opacity-70 uppercase tracking-wider mb-0.5">Gerçek Zamanlı</div>
                        <div className="text-sm font-bold">Canlı Tarama</div>
                    </div>
                </button>
            </div>

            {/* Main Action Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Shared Lists Widget */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600">
                                <ShoppingBag size={20} />
                            </div>
                            <h3 className="font-bold dark:text-white">Size Gelen Listeler</h3>
                        </div>
                        <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded text-xs font-bold">
                            {activeLists.length} Aktif
                        </span>
                    </div>
                    <div className="p-6">
                        {activeLists.length === 0 ? (
                            <div className="text-center py-8">
                                <div className="text-gray-400 mb-2">Henüz paylaşılan bir liste yok.</div>
                                <p className="text-xs text-gray-500 italic">Eşiniz bir liste oluşturduğunda buraya düşecek.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {activeLists.map(list => (
                                    <Link
                                        key={list.id}
                                        to={`/shared/${list.shareToken}`}
                                        className="flex items-center justify-between p-4 rounded-xl border border-gray-50 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800 transition group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center text-gray-500">
                                                <ShoppingBag size={18} />
                                            </div>
                                            <div>
                                                <div className="font-semibold dark:text-white">{list.name}</div>
                                                <div className="text-xs text-gray-500">{list.items?.length || 0} Ürün</div>
                                            </div>
                                        </div>
                                        <ChevronRight size={18} className="text-gray-400 group-hover:translate-x-1 transition-transform" />
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Info / Tips */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 p-6">
                    <h3 className="font-bold mb-4 flex items-center gap-2 dark:text-white">
                        <Gift size={20} className="text-orange-500" />
                        Gıda Güvenliği & Tasarruf
                    </h3>
                    <div className="space-y-4">
                        <div className="p-4 bg-orange-50 dark:bg-orange-900/10 rounded-xl border border-orange-100 dark:border-orange-900/30">
                            <p className="text-sm text-orange-800 dark:text-orange-200 leading-relaxed">
                                💡 **Biliyor muydunuz?** <br />
                                {partnerData?.partner?.allergies?.length > 0 ? (
                                    <>Partnerinizin hassasiyetine göre (<b>{partnerData.partner.allergies.join(', ')}</b>) özel uyarılar artık market listelerinde otomatik olarak karşına çıkacak!</>
                                ) : (
                                    <><b>Gluten</b> gibi hassasiyetlere karşı özel uyarılar artık market listelerinde otomatik olarak karşına çıkacak!</>
                                )}
                            </p>
                        </div>
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30">
                            <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
                                🛡️ **Hızlı Market Modu** <br />
                                Listeleri açtığında sağ üstteki 🔄 ikonuna basarak partnerinin önerdiği alternatif ürünleri görebilirsin.
                            </p>
                        </div>
                    </div>
                </div>

            </div>

            {/* Footer Branding */}
            <div className="flex items-center justify-center gap-2 text-gray-400 text-xs py-4">
                <LayoutDashboard size={14} />
                <span>Mikro BBTV • Güçlü Yardımcı Şef Paneli</span>
            </div>

            {/* Allergen Scanner Modal */}
            <AllergenScannerModal
                isOpen={isScannerOpen}
                onClose={() => setIsScannerOpen(false)}
                initialMode={scannerMode}
            />
        </div>
    );
};

export default PartnerDashboard;
